/**
 * Faithful port of the iOS scratch-off foil to Canvas2D.
 *
 * Two-layer stack (z-order, bottom → top):
 *   1. <img class="reveal">          full-color "reveal" image
 *   2. <canvas class="foil">         silver gradient + multiplied outline +
 *                                    scratch mask + particles + shine sweep.
 *                                    Eats pointer events; everything below
 *                                    is non-interactive.
 *
 * Foil composition mirrors the iOS implementation in ScratchConversion.tsx
 * (refer to that for original tuning rationale):
 *   - 5-stop diagonal linear gradient with hex values
 *     #B6BEC8, #ECEFF3, #A0A8B2, #DCE1E7, #A8B0BA at [0, 0.28, 0.5, 0.72, 1]
 *   - Outline composited via globalCompositeOperation='multiply'
 *   - Pointer scratches stamp white circles into a separate mask canvas;
 *     mask is applied to the foil each frame via 'destination-out'
 *   - Scratched-cell grid (4px cells) gates particle emission AND fires the
 *     diagonal shine sweep once when ≥98.5% of cells are scratched
 *   - 32-particle aluminum-dust pool, gravity 1200 px/s², emission 1 per
 *     11 px of finger travel on unscratched cells
 *
 * Canvas is allocated at devicePixelRatio (logical CSS dims × DPR) so
 * scratched edges look crisp on retina, matching the iOS lesson where
 * not doing this caused the laggy-outline bug.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Props {
  outlineUrl: string;
  colorUrl: string;
  /** API-provided aspect ratio (width / height of the outline image)
   *  passed in by the parent. When non-null, the card uses it from the
   *  very first paint — no 1:1 default that would later "jump" to the
   *  real aspect when images finish loading. Null when the share row
   *  predates migration #24 and the API returns no dims; we fall back
   *  to the old behavior of measuring after image preload. */
  initialAspect: number | null;
  /** Fires once when the user begins scratching (first pointer-down on
   *  the foil). Parent uses this to fade out the finger-hint animation
   *  layered over the canvas. Subsequent strokes don't re-fire. */
  onScratchStart?: () => void;
  /** Fires once when the scratched-cell ratio crosses SHINE_THRESHOLD
   *  (~98.5%) — i.e., the moment the diagonal shine sweep starts.
   *  Parent uses this to swap the "Scratch off to reveal" hint for the
   *  "Make Your Own in Linify" CTA. Like `onScratchStart`, fires at
   *  most once per ScratchPlayer mount. */
  onScratchComplete?: () => void;
  /** Opt-in testing flag. When true, the moment the scratched ratio
   *  crosses SHINE_THRESHOLD (98.5%) the engine programmatically
   *  fills the rest of the mask + marks every grid cell scratched,
   *  so on the very next render frame the foil reads as 100%
   *  cleared. Visually: the user scratches up to 98.5% by hand, then
   *  the last 1.5% snaps away in one frame and the shine sweep runs
   *  over a fully revealed card. Useful for tightening the reveal
   *  moment (recipients don't have to chase the stubborn stragglers
   *  at the rounded corners) and for testing the finalization
   *  animation against a clean reveal.
   *
   *  Toggle without rebuild: append `?autofinish=1` to the share URL
   *  in App.tsx, where IS_AUTO_FINISH is sourced. Defaults to false
   *  — production share viewer keeps hand-cleared edge texture. */
  autoFinish?: boolean;
  /** Scratched-cell fraction (0–1) at which the finale fires — the
   *  shine sweep + auto-finish dissolve + onScratchComplete. Defaults
   *  to SHINE_THRESHOLD (0.985). ?m=1 capture mode passes a lower value
   *  so the reveal + confetti trigger with less hand-scratching. */
  completeThreshold?: number;
}

// ── Tuning constants (mirror iOS) ────────────────────────────────────
const FOIL_STOPS: Array<[number, string]> = [
  [0.00, '#B6BEC8'],
  [0.28, '#ECEFF3'],
  [0.50, '#A0A8B2'],
  [0.72, '#DCE1E7'],
  [1.00, '#A8B0BA'],
];

const GRID_CELL_PX = 4;
const SHINE_THRESHOLD = 0.985;
/** Delay (ms) between crossing SHINE_THRESHOLD and firing
 *  `onScratchComplete`. The shine sweep + autofinish both fire
 *  immediately at threshold (foil clears, gradient sweep runs) so
 *  the reveal is instantaneous; then this delay holds before the
 *  parent's celebration chain (confetti + card-pop + CTA fade-in)
 *  arrives as a distinct beat. Tuned 2026-06-09 per user feedback —
 *  500 ms felt right for "reveal first, then party." */
const COMPLETE_DELAY_MS = 500;
/** Duration (ms) over which the autofinish progressively punches out
 *  the remaining un-scratched foil. At threshold, instead of the
 *  earlier instant snap-to-clear, the render loop layers an
 *  additional `destination-out` fill with `globalAlpha = elapsed /
 *  AUTOFINISH_DURATION_MS` each frame, so foil dissolves over this
 *  window. Tuned to finish in ~400 ms — short enough to feel snappy,
 *  long enough that the particles spawned at the un-scratched cell
 *  centers have time to read as "the foil burst into dust." */
const AUTOFINISH_DURATION_MS = 400;
const SHINE_DURATION_MS = 1500;
const SHINE_BAND_HALF_FRAC = 0.16;
const SHINE_PEAK_ALPHA = 0.45;

const BRUSH_R_FRAC = 0.05; // brush radius as a fraction of card width
const STAMP_STEP_FRAC = 0.55; // along-segment stamp spacing × brush radius
const STAMP_VERTS = 20; // jagged-polygon vertices per stamp (iOS uses 20)

// Particles
const MAX_PARTICLES = 32;
const EMISSION_STEP_PX = 11;
const MAX_SPAWNS_PER_EVENT = 6;
const GRAVITY_PX_S2 = 1200;
const INIT_VY_MIN = -90;
const INIT_VY_MAX = -10;
const INIT_VX_RANGE = 55;
const DRAG_RATE = 0.8;
const SCALE_MIN = 1.2;
const SCALE_MAX = 2.6;
const LIFE_MIN_MS = 600;
const LIFE_MAX_MS = 1300;
const PARTICLE_COLORS = ['#5A6068', '#4C5158', '#6B7280', '#3F434B', '#757B85'];
const POSITION_JITTER = 1.5;

// 5 jagged-polygon templates, unit radius (~1) — particle scale is applied
// at render. Matches iOS's irregular-shape rendering for "real foil dust".
const JAGGED_TEMPLATES: Array<Array<[number, number]>> = (() => {
  const templates: Array<Array<[number, number]>> = [];
  for (let i = 0; i < 5; i++) {
    const sides = 5 + Math.floor(Math.random() * 3); // 5-7 vertices
    const verts: Array<[number, number]> = [];
    for (let s = 0; s < sides; s++) {
      const angle = (s / sides) * Math.PI * 2;
      const r = 0.6 + Math.random() * 0.8;
      verts.push([Math.cos(angle) * r, Math.sin(angle) * r]);
    }
    templates.push(verts);
  }
  return templates;
})();

interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  rotVel: number;
  ageMs: number;
  lifeMs: number;
  scale: number;
  color: string;
  templateIdx: number;
}

function makeInactiveParticle(): Particle {
  return {
    active: false, x: 0, y: 0, vx: 0, vy: 0, rot: 0, rotVel: 0,
    ageMs: 0, lifeMs: 0, scale: 1, color: PARTICLE_COLORS[0], templateIdx: 0,
  };
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function makeFoilGradient(ctx: CanvasRenderingContext2D, w: number, h: number) {
  // Diagonal: top-left → bottom-right
  const g = ctx.createLinearGradient(0, 0, w, h);
  for (const [pos, color] of FOIL_STOPS) g.addColorStop(pos, color);
  return g;
}

/** Pre-render the foil base (gradient + maybe multiplied outline) to an
 *  offscreen canvas sized at DEVICE pixels so the outline doesn't get
 *  re-scaled when blitted into the foil canvas later (which would soften
 *  it). The caller is responsible for drawing this 1:1 (transform-reset)
 *  so the physical pixels land 1-for-1 on the foil canvas's backing
 *  store.
 *
 *  `outline` is optional — when null/undefined, we render just the silver
 *  gradient. Used during the initial mount when the outline image hasn't
 *  loaded yet so the user sees foil immediately instead of a blank white
 *  canvas. A re-bake fires once the outline arrives. */
/** Compute a "contain"-style fit rect for an image of intrinsic aspect
 *  `imgAr` (width/height) into a canvas of dims wPx × hPx. Returns the
 *  destination rect to pass to drawImage so the image preserves its
 *  aspect ratio, centered, with letterbox padding on whichever axis is
 *  shorter than the canvas.
 *
 *  Used by BOTH the foil prebake (outline image) AND the render loop's
 *  destination-over color step so the outline you scratch against and
 *  the colored reveal underneath line up pixel-for-pixel. Prior to
 *  2026-06-19 the color step stretched to fill (0, 0, wPx, hPx) while
 *  the outline letterboxed, producing the user-reported "outline and
 *  reveal didn't match in dimensions or size" bug on legacy shares
 *  (`width:null,height:null` API rows where the card defaults to
 *  aspect=1 in CSS but the actual images are portrait). */
function fitInside(
  imgAr: number,
  wPx: number,
  hPx: number,
): { dx: number; dy: number; dw: number; dh: number } {
  const cardAr = wPx / hPx;
  let dw = wPx, dh = hPx, dx = 0, dy = 0;
  if (imgAr > cardAr) {
    dh = wPx / imgAr;
    dy = (hPx - dh) / 2;
  } else {
    dw = hPx * imgAr;
    dx = (wPx - dw) / 2;
  }
  return { dx, dy, dw, dh };
}

function prebakeFoil(
  outline: HTMLImageElement | null,
  wPx: number,
  hPx: number
): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = wPx;
  cv.height = hPx;
  const ctx = cv.getContext('2d')!;
  // Higher-quality downscale of the source outline — Chrome/Safari respect
  // these hints for `drawImage` resampling on offscreen canvases.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // Base gradient — always.
  ctx.fillStyle = makeFoilGradient(ctx, wPx, hPx);
  ctx.fillRect(0, 0, wPx, hPx);
  // Outline (when available) composited via multiply — same recipe as iOS.
  if (outline) {
    ctx.globalCompositeOperation = 'multiply';
    const ar = outline.naturalWidth / outline.naturalHeight;
    const { dx, dy, dw, dh } = fitInside(ar, wPx, hPx);
    ctx.drawImage(outline, dx, dy, dw, dh);
    ctx.globalCompositeOperation = 'source-over';
  }
  return cv;
}

/** Build the 20-vertex jagged polygon for a single brush stamp. Identical
 *  noise math to the iOS worklet (`stampRoughWorklet` in
 *  ScratchConversion.tsx) so the visual tear pattern matches: per-vertex
 *  radius varies 0.85 → 1.15 × brushR, seeded by position + index.
 *
 *  Returns a fresh Path2D — caller fills it with whatever paint/composite
 *  mode they want. Center (x,y) is the stamp's geometric center in
 *  whatever coord space the caller uses. */
function makeJaggedStampPath(x: number, y: number, brushR: number): Path2D {
  const path = new Path2D();
  for (let i = 0; i < STAMP_VERTS; i++) {
    const angle = (i / STAMP_VERTS) * Math.PI * 2;
    const seed = x * 0.371 + y * 0.713 + i * 7.31;
    const noise = Math.abs(Math.sin(seed) * 43758.5453) % 1;
    const r = brushR * (0.85 + noise * 0.30);
    const px = x + Math.cos(angle) * r;
    const py = y + Math.sin(angle) * r;
    if (i === 0) path.moveTo(px, py);
    else path.lineTo(px, py);
  }
  path.closePath();
  return path;
}

export function ScratchPlayer({
  outlineUrl,
  colorUrl,
  initialAspect,
  onScratchStart,
  onScratchComplete,
  autoFinish = false,
  completeThreshold,
}: Props) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const foilRef = useRef<HTMLCanvasElement | null>(null);
  /** Ref-mirror of the outlineImg state so the main render-loop effect
   *  (which runs once on mount) can always read the latest value without
   *  including outlineImg in its deps. A separate effect below syncs
   *  state → ref + triggers a re-bake when the outline arrives. */
  const outlineImgRef = useRef<HTMLImageElement | null>(null);
  /** Same ref-mirror pattern for the color image. The render loop reads
   *  it every frame to composite into the scratched-out holes of the
   *  foil (via destination-over) — single-canvas approach replacing the
   *  prior separate <img className="reveal"> layer, which caused
   *  rounded-corner anti-alias bleed (2026-06-10 fix). Null until the
   *  color image finishes loading; render skips the dest-over step
   *  when null so the foil canvas just shows the foil. */
  const colorImgRef = useRef<HTMLImageElement | null>(null);
  // Ref-mirror the onScratchStart / onScratchComplete props so the main
  // engine effect (which runs once on mount and reads from closures)
  // always sees the latest callback identity without needing to be in
  // its deps array — including those callbacks in deps would tear down
  // the engine on every parent re-render and re-introduce the "first
  // tap doesn't scratch" bug. The .current calls below are no-ops when
  // the parent hasn't supplied a callback.
  const onScratchStartRef = useRef(onScratchStart);
  const onScratchCompleteRef = useRef(onScratchComplete);
  onScratchStartRef.current = onScratchStart;
  onScratchCompleteRef.current = onScratchComplete;
  // Same ref-mirror pattern for autoFinish so a parent that flips the
  // prop mid-session (e.g. URL change driven via SPA navigation, not
  // currently a thing but cheap to support) is honored without
  // tearing down the engine. The engine reads from .current inside
  // maybeStartShine just before firing onScratchComplete.
  const autoFinishRef = useRef(autoFinish);
  autoFinishRef.current = autoFinish;
  // Same ref-mirror for the completion threshold so the once-on-mount
  // engine reads the latest value in maybeStartShine without teardown.
  const completeThresholdRef = useRef(completeThreshold);
  completeThresholdRef.current = completeThreshold;
  /** Single-fire guard for onScratchStart — survives across the engine's
   *  many pointer events, never reset between strokes. (The parent only
   *  cares that scratching has begun, not which finger touched.) */
  const startNotifiedRef = useRef(false);
  /** Exposed by the main effect so the outline-arrival watcher can ask
   *  for a fresh prebake without tearing down the entire engine. */
  const requestRebakeRef = useRef<(() => void) | null>(null);

  // Initial aspect from the API (via props). Falls back to 1:1 only when
  // the share row predates migration #24. The post-image-load effect
  // below still updates aspect if the actual image dims diverge — but on
  // the common path (modern shares) the aspect is correct from frame 1,
  // killing the square-then-jump layout shift the user reported.
  const [aspectRatio, setAspectRatio] = useState<number>(
    initialAspect && initialAspect > 0 ? initialAspect : 1
  );
  const [outlineImg, setOutlineImg] = useState<HTMLImageElement | null>(null);
  const [colorImg, setColorImg] = useState<HTMLImageElement | null>(null);
  const [hintDismissed, setHintDismissed] = useState(false);
  /** False until the foil canvas has painted its first frame (gradient
   *  + multiplied outline). Originally gated the reveal `<img>`'s
   *  visibility (now removed in single-canvas refactor 2026-06-10);
   *  still gates the scratch-finger hint, so the 👆 emoji never
   *  appears over a blank/white canvas before the foil commits. */
  const [foilPainted, setFoilPainted] = useState(false);

  // Preload both images so we know aspect ratios before composing.
  useEffect(() => {
    let cancelled = false;
    const o = new Image();
    const c = new Image();
    // crossOrigin='anonymous' so the canvas isn't tainted when we
    // drawImage these images into the foil canvas (single-canvas
    // composite — see render loop's destination-over color step).
    // Requires the R2 bucket to send Access-Control-Allow-Origin
    // headers; CORS rule applied via Cloudflare API 2026-06-10.
    // Must be set BEFORE .src so the request goes out with the
    // right credentials mode.
    o.crossOrigin = 'anonymous';
    c.crossOrigin = 'anonymous';

    // Cache-buster removed 2026-06-10 after a manual CF edge purge of
    // storage.linify.afappstudio.com. Previously needed (`?v=1`)
    // because pre-CORS-rule cached responses sat at the edge with
    // `cache-control: immutable, max-age=31536000` — a year — without
    // Access-Control-Allow-Origin headers, making crossOrigin loads
    // fail. After the purge, the next fetch per URL is a MISS that
    // picks up the CORS-enabled R2 response and re-caches with ACAO.
    // If we ever need to bust again (CORS policy change, take-down,
    // bad-upload recovery), re-add a `?v=N` suffix and bump N.
    const loadOne = (img: HTMLImageElement, src: string) =>
      new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image failed to load: ' + src));
        img.src = src;
      });

    Promise.all([loadOne(o, outlineUrl), loadOne(c, colorUrl)])
      .then(() => {
        if (cancelled) return;
        setOutlineImg(o);
        setColorImg(c);
        // Pick the aspect ratio from whichever image we have; in practice
        // both should match (server-side they're derived from the same
        // canvas). Fall back to 1:1 if neither has loaded yet.
        const ar = o.naturalWidth && o.naturalHeight
          ? o.naturalWidth / o.naturalHeight
          : 1;
        setAspectRatio(ar);
      })
      .catch((err) => {
        // Don't crash on a single image failing — show what we have.
        console.warn('[scratch-web] image preload failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, [outlineUrl, colorUrl]);

  // Sync outlineImg state → ref + request a re-bake when it arrives. The
  // main engine effect below reads outlineImg via the ref so it can run
  // exactly once on mount (no teardown/rebuild churn between gradient-only
  // and gradient+outline phases).
  useEffect(() => {
    outlineImgRef.current = outlineImg;
    if (outlineImg && requestRebakeRef.current) {
      requestRebakeRef.current();
    }
  }, [outlineImg]);

  // Same pattern for color image — the render loop reads it via the
  // ref every frame. No re-bake needed (color isn't in the prebaked
  // foilBaked canvas; the render loop composites color via
  // destination-over into the foil's scratched holes each frame).
  // Just need needsFrame to fire so the loop picks up the new color
  // image on the next tick.
  useEffect(() => {
    colorImgRef.current = colorImg;
    // Tickle the render loop — same hook as the rebake requester.
    if (colorImg && requestRebakeRef.current) {
      requestRebakeRef.current();
    }
  }, [colorImg]);

  // Main render loop — runs once on mount so the foil gradient appears
  // IMMEDIATELY (no blank white frame), then the outline gets composited
  // in via re-bake when the image finishes downloading.
  //
  // useLayoutEffect (not useEffect) so the initial foil paint commits
  // BEFORE the browser's first paint of the canvas — without this, the
  // canvas would appear empty (over a white card background) for one
  // frame before RAF schedules the foil bake.
  useLayoutEffect(() => {
    const card = cardRef.current;
    const foilCanvas = foilRef.current;
    if (!card || !foilCanvas) return;

    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));

    // ─── Sizes ───────────────────────────────────────────────────────
    function getLogicalSize() {
      const rect = card!.getBoundingClientRect();
      return { wLog: rect.width, hLog: rect.height };
    }

    let { wLog, hLog } = getLogicalSize();
    // ceil (not round) so the canvas raster is always at least as
    // many device pixels as the display dims demand. With Math.round,
    // a card with fractional CSS height like 286.4 (e.g. landscape
    // 1.25 aspect on 358 css px wide) rounds DOWN to 286 raster
    // pixels — leaving the canvas 0.4 css px SHORTER than its
    // display box. The browser then shows the card's white
    // background through the gap, visible as a 1px white line under
    // the foil. Math.ceil rounds up instead — raster is now slightly
    // TALLER than display, browser scales it down by <1%, no gap.
    // Reported by user 2026-06-10 on landscape shares; portrait
    // shares like awgx585jq6 happened to round up (447.5 → 448) so
    // they didn't trigger this bug. Same fix applied to the resize
    // handler below (~L968).
    let wPx = Math.ceil(wLog * dpr);
    let hPx = Math.ceil(hLog * dpr);
    let brushR = wLog * BRUSH_R_FRAC;
    let cellsX = Math.ceil(wLog / GRID_CELL_PX);
    let cellsY = Math.ceil(hLog / GRID_CELL_PX);
    let totalCells = cellsX * cellsY;
    let scratchedCount = 0;
    let grid = new Uint8Array(totalCells);

    function configureCanvas(c: HTMLCanvasElement) {
      c.width = wPx;
      c.height = hPx;
      // Display 2 px wider/taller than the card box (with -1 px
      // offset on top/left, via the CSS .card canvas rule). The
      // raster stays at wPx × hPx — browser stretches it ~0.5 % to
      // fill the inflated display. Net effect: the canvas's anti-
      // aliased edge falls OUTSIDE the card's overflow:hidden +
      // border-radius clip box, so what shows at the card's
      // boundary is a fully-opaque interior pixel of the canvas
      // (no anti-aliasing → no bright edge sliver). See the
      // .card canvas comment in styles.css for the full rationale.
      c.style.width = (wLog + 2) + 'px';
      c.style.height = (hLog + 2) + 'px';
    }
    configureCanvas(foilCanvas);

    // Foil canvas drawing context — DPR-scaled so we draw in logical
    // coords throughout (matches iOS where we operated in points).
    const foilCtx = foilCanvas.getContext('2d')!;
    foilCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // ─── Pre-bake the foil base (gradient + maybe multiplied outline) ──
    // Sized at DEVICE pixels so the outline doesn't get re-scaled when
    // blitted onto the foil canvas under its DPR transform. Blit with
    // setTransform-identity so device-pixel sizes land 1:1.
    //
    // On first mount the outline image likely hasn't loaded yet — we
    // bake gradient-only and re-bake later (see requestRebakeRef wired
    // a few lines below). Net effect: foil is visible by next paint.
    let foilBaked = prebakeFoil(outlineImgRef.current, wPx, hPx);

    // ─── Immediate first paint ───────────────────────────────────────
    // Synchronously commit the foil bake to the foil canvas BEFORE we
    // return from the layout-effect (and before the browser's first
    // paint). Without this, RAF would do the first paint one frame
    // later, leaving the canvas blank/white for ~16ms after mount.
    foilCtx.save();
    foilCtx.setTransform(1, 0, 0, 1, 0, 0);
    foilCtx.drawImage(foilBaked, 0, 0);
    foilCtx.restore();
    setFoilPainted(true);

    // ─── Mask canvas: where we accumulate scratch alpha ──────────────
    // Drawn each frame onto the foil with 'destination-out' to punch
    // holes. ALSO sized at device pixels; stamps land 1:1 with the foil
    // bake so the alpha mask resolution matches the prebake.
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = wPx;
    maskCanvas.height = hPx;
    const maskCtx = maskCanvas.getContext('2d')!;
    // The mask is drawn in DEVICE pixel coords. We track stamps in logical
    // coords (where pointer events live) and scale up by dpr when
    // committing to the mask context.
    maskCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Start fully transparent (no scratches).


    // ─── Particles ────────────────────────────────────────────────────
    const particles: Particle[] = Array.from({ length: MAX_PARTICLES }, makeInactiveParticle);
    let nextParticleSlot = 0;

    function gridIndex(x: number, y: number): number {
      const cx = Math.floor(x / GRID_CELL_PX);
      const cy = Math.floor(y / GRID_CELL_PX);
      if (cx < 0 || cx >= cellsX || cy < 0 || cy >= cellsY) return -1;
      return cy * cellsX + cx;
    }

    function isFoiledAt(x: number, y: number): boolean {
      const idx = gridIndex(x, y);
      if (idx < 0) return false;
      return grid[idx] === 0;
    }

    function spawnParticleAt(x: number, y: number) {
      const slot = nextParticleSlot;
      nextParticleSlot = (nextParticleSlot + 1) % MAX_PARTICLES;
      const p = particles[slot];
      p.active = true;
      p.x = x + rand(-POSITION_JITTER, POSITION_JITTER);
      p.y = y + rand(-POSITION_JITTER, POSITION_JITTER);
      p.vx = rand(-INIT_VX_RANGE, INIT_VX_RANGE);
      p.vy = rand(INIT_VY_MIN, INIT_VY_MAX);
      p.rot = Math.random() * Math.PI * 2;
      p.rotVel = rand(-4.5, 4.5);
      p.ageMs = 0;
      p.lifeMs = rand(LIFE_MIN_MS, LIFE_MAX_MS);
      p.scale = rand(SCALE_MIN, SCALE_MAX);
      p.color = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
      p.templateIdx = Math.floor(Math.random() * JAGGED_TEMPLATES.length);
    }

    // Spacing-based emission walker. Returns the new accumulated travel
    // remainder so the caller can keep state across move events.
    function walkEmissions(
      lx: number, ly: number, x: number, y: number, leftover: number
    ): number {
      const dx = x - lx, dy = y - ly;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist === 0) return leftover;
      let traveled = leftover;
      let spawns = 0;
      // Walk along the segment in steps of EMISSION_STEP_PX
      let pos = -leftover; // negative = we already traveled `leftover` before this segment
      while (pos < dist && spawns < MAX_SPAWNS_PER_EVENT) {
        const t = pos < 0 ? 0 : pos / dist;
        const ex = lx + dx * t;
        const ey = ly + dy * t;
        if (isFoiledAt(ex, ey)) {
          spawnParticleAt(ex, ey);
          spawns++;
        }
        pos += EMISSION_STEP_PX;
      }
      traveled = (pos - dist);
      // Wrap traveled into [0, EMISSION_STEP_PX)
      while (traveled < 0) traveled += EMISSION_STEP_PX;
      while (traveled >= EMISSION_STEP_PX) traveled -= EMISSION_STEP_PX;
      return traveled;
    }

    // ─── Mark grid cells as scratched along a segment ────────────────
    function markScratched(lx: number, ly: number, x: number, y: number) {
      const dx = x - lx, dy = y - ly;
      const dist = Math.hypot(dx, dy);
      const step = brushR * STAMP_STEP_FRAC;
      const n = Math.max(1, Math.ceil(dist / step));
      const r2 = brushR * brushR;
      for (let i = 1; i <= n; i++) {
        const t = n === 0 ? 1 : i / n;
        const sx = lx + dx * t;
        const sy = ly + dy * t;
        const minCx = Math.max(0, Math.floor((sx - brushR) / GRID_CELL_PX));
        const maxCx = Math.min(cellsX - 1, Math.floor((sx + brushR) / GRID_CELL_PX));
        const minCy = Math.max(0, Math.floor((sy - brushR) / GRID_CELL_PX));
        const maxCy = Math.min(cellsY - 1, Math.floor((sy + brushR) / GRID_CELL_PX));
        for (let cy = minCy; cy <= maxCy; cy++) {
          for (let cx = minCx; cx <= maxCx; cx++) {
            const px = (cx + 0.5) * GRID_CELL_PX;
            const py = (cy + 0.5) * GRID_CELL_PX;
            const ddx = px - sx;
            const ddy = py - sy;
            if (ddx * ddx + ddy * ddy <= r2) {
              const idx = cy * cellsX + cx;
              if (grid[idx] === 0) {
                grid[idx] = 1;
                scratchedCount++;
              }
            }
          }
        }
      }
    }

    // ─── Stamp scratch into the mask canvas ──────────────────────────
    // Match iOS: per-stamp a 20-vertex jagged polygon, identical noise
    // math. Each stamp is opaque white — no soft radial gradient — so
    // the dstOut against the foil punches a hard-edged hole that reads
    // as "scraped foil". Coordinates are in LOGICAL units (matches the
    // pointer/grid math); the mask context has a DPR transform set.
    function stampMaskRough(x: number, y: number) {
      const path = makeJaggedStampPath(x, y, brushR);
      maskCtx.fillStyle = 'rgba(255,255,255,1)';
      maskCtx.fill(path);
    }
    function stampMaskSegment(lx: number, ly: number, x: number, y: number) {
      const dx = x - lx, dy = y - ly;
      const dist = Math.hypot(dx, dy);
      const step = brushR * STAMP_STEP_FRAC;
      const n = Math.max(1, Math.ceil(dist / step));
      for (let i = 1; i <= n; i++) {
        const t = i / n;
        stampMaskRough(lx + dx * t, ly + dy * t);
      }
    }

    // ─── Shine sweep ─────────────────────────────────────────────────
    let shineAt: number | null = null; // when it started (perf.now ms)
    let shineFired = false;
    /** setTimeout handle for the deferred onScratchComplete fire. Held
     *  here so the cleanup function below can clear it if the engine
     *  tears down (component unmounts, share navigates away, etc.)
     *  during the 500 ms gap — prevents firing the parent callback
     *  on an unmounted tree. Null until maybeStartShine schedules it. */
    let completeTimeoutId: ReturnType<typeof setTimeout> | null = null;
    /** Set when autofinish begins (perf.now ms); null at rest. The
     *  render loop reads this and layers a progressive destination-out
     *  white fill on the foil over AUTOFINISH_DURATION_MS ms before
     *  baking the cleared state into mask + grid and clearing this. */
    let autoFinishStartTime: number | null = null;
    function maybeStartShine() {
      if (shineFired) return;
      // ?m=1 capture mode lowers this via the completeThreshold prop;
      // normal recipients use SHINE_THRESHOLD (0.985).
      const threshold = completeThresholdRef.current ?? SHINE_THRESHOLD;
      if (scratchedCount / totalCells < threshold) return;
      shineFired = true;
      shineAt = performance.now();

      // Auto-finish (prop, gated on autoFinishRef.current) — fade
      // out the remaining ~1.5% of foil over AUTOFINISH_DURATION_MS
      // AND spawn a burst of aluminum-dust particles at the still-
      // unscratched cell centers, so the foil reads as "burst into
      // dust" instead of just snapping away. Two-part flow:
      //
      //   1. Sample MAX_PARTICLES (32) still-unscratched cells via a
      //      partial Fisher-Yates shuffle. Spread across the card so
      //      the burst visually covers all the remaining foil regions
      //      (rounded corners, brush-edge stragglers, etc.). For each
      //      sampled cell, convert (cellIdx → cx/cy → centerpoint in
      //      logical px) and `spawnParticleAt(px, py)` — gives the
      //      particle pool the right starting positions; the existing
      //      gravity / drag / lifeMs physics then carries the burst.
      //
      //   2. Stamp `autoFinishStartTime` so the render loop layers a
      //      progressive `destination-out` white fill on the foil
      //      canvas each frame with `globalAlpha = elapsed /
      //      AUTOFINISH_DURATION_MS`. Foil dissolves over ~400 ms;
      //      once progress hits 1.0 the render loop commits the
      //      cleared state into the grid + mask and clears the
      //      autoFinishStartTime.
      //
      // Single-fire by construction — `shineFired` guards the path,
      // so even if the user keeps stroking afterwards no second
      // burst spawns.
      if (autoFinishRef.current) {
        // Collect indices of still-unscratched cells.
        const unscratched: number[] = [];
        for (let i = 0; i < grid.length; i++) {
          if (grid[i] === 0) unscratched.push(i);
        }
        // Partial Fisher-Yates: sample up to MAX_PARTICLES cells
        // without bias. Stops at min(MAX_PARTICLES, unscratched.length)
        // — handles the edge case of fewer remaining cells than
        // particle slots without overflow.
        const sampleSize = Math.min(MAX_PARTICLES, unscratched.length);
        for (let k = 0; k < sampleSize; k++) {
          const j = k + Math.floor(Math.random() * (unscratched.length - k));
          const tmp = unscratched[k];
          unscratched[k] = unscratched[j];
          unscratched[j] = tmp;
          const cellIdx = unscratched[k];
          const cx = cellIdx % cellsX;
          const cy = (cellIdx - cx) / cellsX;
          spawnParticleAt(
            (cx + 0.5) * GRID_CELL_PX,
            (cy + 0.5) * GRID_CELL_PX,
          );
        }
        // Kick the foil-fade animation. The render loop sees this on
        // its next frame and starts layering the progressive punch-
        // out — also forces a frame in case the render loop was
        // dormant (no in-flight particles, no shine yet — though
        // shineAt was just set above, so RAF will keep ticking).
        autoFinishStartTime = performance.now();
        needsFrame = true;
      }

      // Notify the parent that scratching is complete — deferred by
      // COMPLETE_DELAY_MS (500 ms) so the celebration animation
      // (confetti + card-pop + CTA fade-in) sits a beat behind the
      // threshold-crossing moment. The shine sweep + autofinish
      // both already fired above (immediate visual reveal); this
      // delay lets that read as "the foil is gone" before the
      // confetti pops over the top. Same single-fire semantics as
      // `shineFired` itself — guaranteed at-most-once per mount.
      completeTimeoutId = setTimeout(() => {
        completeTimeoutId = null;
        onScratchCompleteRef.current?.();
      }, COMPLETE_DELAY_MS);
    }
    function drawShineIfActive(now: number) {
      if (shineAt == null) return false;
      const t = (now - shineAt) / SHINE_DURATION_MS;
      if (t >= 1) {
        shineAt = null;
        return false;
      }
      // Guard t < 0: a RAF frame's timestamp can be marginally earlier
      // than the performance.now() captured when the shine was triggered
      // (same-frame ordering — very likely under a burst of synchronous
      // pointer events, possible on slow devices). Negative t pushes the
      // band center below -bandHalf, which drove c1 = Math.min(1,
      // (center+bandHalf)/diag) NEGATIVE (min only upper-clamps), so
      // addColorStop received an offset < 0 and threw IndexSizeError —
      // which killed the whole render loop and froze the auto-finish
      // dissolve. The sweep isn't visible before t=0 anyway, so skip this
      // frame and keep the RAF alive (return true) until t crosses 0.
      if (t <= 0) return true;
      // Diagonal band sweeping top-left → bottom-right.
      const diag = Math.hypot(wLog, hLog);
      const bandHalf = diag * SHINE_BAND_HALF_FRAC;
      // Band center moves from (-bandHalf) to (diag + bandHalf) over t∈[0,1].
      const center = -bandHalf + (diag + 2 * bandHalf) * t;
      // Sin-wave alpha curve so the band fades in/out smoothly.
      const alpha = SHINE_PEAK_ALPHA * Math.sin(Math.PI * t);
      foilCtx.save();
      foilCtx.globalCompositeOperation = 'lighter';
      // Project alpha along the canvas diagonal via a linear gradient —
      // the gradient implements the (1,1)/sqrt(2) axis projection
      // implicitly. Band is centered on the moving `center` value, with
      // bandHalf radius on each side.
      const grad = foilCtx.createLinearGradient(0, 0, wLog, hLog);
      const c0 = Math.max(0, (center - bandHalf) / diag);
      const c1 = Math.min(1, (center + bandHalf) / diag);
      const cm = Math.max(0, Math.min(1, center / diag));
      grad.addColorStop(c0, 'rgba(255,255,255,0)');
      grad.addColorStop(cm, `rgba(255,255,255,${alpha})`);
      grad.addColorStop(c1, 'rgba(255,255,255,0)');
      foilCtx.fillStyle = grad;
      foilCtx.fillRect(0, 0, wLog, hLog);
      foilCtx.restore();
      return true;
    }

    // ─── Render loop ─────────────────────────────────────────────────
    let lastFrameMs = performance.now();
    let rafId = 0;
    let needsFrame = true;
    // Fired exactly once after the first composit. Flips foilPainted
    // → true in React state so the scratch-finger hint can mount over
    // a now-painted canvas (no flash over an empty canvas).
    let firstPaintFired = false;

    function renderFrame(now: number) {
      const dt = (now - lastFrameMs) / 1000;
      lastFrameMs = now;

      // ── Particles physics ──
      let anyParticleAlive = false;
      const dragMul = Math.exp(-DRAG_RATE * dt);
      for (let i = 0; i < MAX_PARTICLES; i++) {
        const p = particles[i];
        if (!p.active) continue;
        anyParticleAlive = true;
        p.ageMs += dt * 1000;
        if (p.ageMs >= p.lifeMs) {
          p.active = false;
          continue;
        }
        p.vy += GRAVITY_PX_S2 * dt;
        p.vx *= dragMul;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.rotVel * dt;
      }

      // ── Composit the foil canvas ──
      foilCtx.clearRect(0, 0, wLog, hLog);
      // Layer 1: blit pre-baked foil base. Identity transform so the
      // device-pixel-sized prebake lands 1:1 — no resampling = crisp
      // outline. Restore after to keep DPR transform for particle math.
      foilCtx.save();
      foilCtx.setTransform(1, 0, 0, 1, 0, 0);
      foilCtx.drawImage(foilBaked, 0, 0);
      // Layer 2: punch out scratched regions via destination-out. Mask
      // also at device pixels so it aligns with the prebake.
      foilCtx.globalCompositeOperation = 'destination-out';
      foilCtx.drawImage(maskCanvas, 0, 0);
      // Layer 2b: autofinish progressive punch-out (when active). Still
      // under `globalCompositeOperation = 'destination-out'` and
      // identity transform from Layer 2, so a fillRect of white at
      // `globalAlpha = progress` subtracts that fraction of foil
      // across the whole canvas. Combined with the user's existing
      // scratch mask from Layer 2, the foil dissolves over
      // AUTOFINISH_DURATION_MS ms. Once progress reaches 1, the
      // remaining grid + mask are baked to fully-scratched state so
      // subsequent frames don't need to re-run this branch.
      if (autoFinishStartTime !== null) {
        const p = Math.min(
          1,
          (now - autoFinishStartTime) / AUTOFINISH_DURATION_MS,
        );
        foilCtx.globalAlpha = p;
        foilCtx.fillStyle = 'white';
        foilCtx.fillRect(0, 0, wPx, hPx);
        foilCtx.globalAlpha = 1;
        if (p >= 1) {
          grid.fill(1);
          scratchedCount = totalCells;
          maskCtx.fillStyle = 'rgba(255,255,255,1)';
          maskCtx.fillRect(0, 0, wLog, hLog);
          autoFinishStartTime = null;
        } else {
          // Keep the RAF ticking until the fade completes.
          needsFrame = true;
        }
      }
      // Layer 3: composite the colored reveal image into the scratched
      // holes via `destination-over`. dest-over draws the source
      // BEHIND the existing destination, so the foil pixels (still on
      // top from Layers 1-2b) stay intact and only the transparent
      // holes (from Layer 2 + 2b's destination-out) get filled with
      // color. Net effect: scratched pixels show colored image; foil
      // pixels still show foil. SINGLE-CANVAS approach — replaces the
      // prior <img className="reveal"> sibling layer that caused
      // rounded-corner image bleed (2026-06-10 fix). The card has
      // exactly ONE descendant now under its `overflow: hidden`
      // rounded clip, so the per-layer-clip-anti-alias bleed
      // mathematically cannot occur. Identity transform from the
      // outer save() is still in effect, so drawImage at (0, 0, wPx,
      // hPx) lays the color image into device-pixel space matching
      // the foilBaked prebake. When colorImg isn't loaded yet, skip
      // — the prebake's gradient covers everything, scratching pre-
      // load reveals card-background white which fades to color
      // once the image arrives on a subsequent frame.
      const colorImgNow = colorImgRef.current;
      if (colorImgNow) {
        foilCtx.globalCompositeOperation = 'destination-over';
        // Letterbox via `fitInside` (same helper the outline prebake
        // uses) so the colored reveal lines up pixel-for-pixel with the
        // outline you scratched against. Stretching to (0,0,wPx,hPx)
        // here was the pre-2026-06-19 bug for legacy shares whose card
        // aspect (defaulted to 1 in CSS when API dims were null)
        // disagreed with the image's actual aspect.
        const cAr = colorImgNow.naturalWidth / colorImgNow.naturalHeight;
        const { dx, dy, dw, dh } = fitInside(cAr, wPx, hPx);
        foilCtx.drawImage(colorImgNow, dx, dy, dw, dh);
      }
      foilCtx.restore();

      // First-paint signal — flip foilPainted in React state. By the
      // time React reads this and re-renders, the foil pixels have
      // been committed to the canvas backing store. Gates the
      // scratch-finger hint's mount (see `foilPainted` state doc).
      if (!firstPaintFired) {
        firstPaintFired = true;
        setFoilPainted(true);
      }
      // Layer 3: draw particles (over the un-scratched foil only — since the
      // foil is now hole-punched, drawing particles on top of holes will
      // simply land them on the colored reveal underneath, which is the
      // desired iOS behavior).
      for (let i = 0; i < MAX_PARTICLES; i++) {
        const p = particles[i];
        if (!p.active) continue;
        const alpha = 1 - (p.ageMs / p.lifeMs);
        if (alpha <= 0) continue;
        foilCtx.save();
        foilCtx.globalAlpha = alpha;
        foilCtx.translate(p.x, p.y);
        foilCtx.rotate(p.rot);
        foilCtx.scale(p.scale, p.scale);
        foilCtx.fillStyle = p.color;
        const template = JAGGED_TEMPLATES[p.templateIdx];
        foilCtx.beginPath();
        foilCtx.moveTo(template[0][0], template[0][1]);
        for (let v = 1; v < template.length; v++) {
          foilCtx.lineTo(template[v][0], template[v][1]);
        }
        foilCtx.closePath();
        foilCtx.fill();
        foilCtx.restore();
      }
      // Layer 4: shine sweep (only while active)
      const shineActive = drawShineIfActive(now);

      // ── Decide whether to keep the RAF running ──
      if (!anyParticleAlive && !shineActive && !needsFrame) {
        rafId = 0;
        return;
      }
      needsFrame = false;
      rafId = requestAnimationFrame(renderFrame);
    }

    function ensureRunning() {
      if (rafId !== 0) return;
      needsFrame = true;
      lastFrameMs = performance.now();
      rafId = requestAnimationFrame(renderFrame);
    }

    // ─── Pointer handlers ────────────────────────────────────────────
    let pressed = false;
    let activePointerId: number | null = null;
    let lastX = NaN, lastY = NaN;
    let emissionLeftover = 0;

    function pointToLogical(e: PointerEvent): { x: number; y: number } {
      // Use the CARD's rect, not the canvas's. The canvas display is
      // intentionally inflated 1 px beyond the card on every side (to
      // push its anti-aliased edge outside the card's clip box, see
      // configureCanvas). Mapping to logical wLog × hLog coords
      // requires the card-box rect, not the inflated canvas rect, so
      // logical (0, 0) lines up with the card's top-left corner.
      const rect = card!.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left),
        y: (e.clientY - rect.top),
      };
    }

    function onPointerDown(e: PointerEvent) {
      if (pressed) return;
      pressed = true;
      activePointerId = e.pointerId;
      try { foilCanvas!.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
      const { x, y } = pointToLogical(e);
      lastX = x; lastY = y;
      emissionLeftover = 0;
      // ORDER MATTERS:
      //   1) emit BEFORE marking — emission walker checks isFoiledAt(),
      //      which would always return false (cell already 1) if we marked
      //      first. Bug discovered 2026-06-07 during browser QA: marking
      //      first results in zero particles ever spawning.
      //   2) THEN stamp visually + mark grid + check shine
      if (isFoiledAt(x, y)) spawnParticleAt(x, y);
      stampMaskRough(x, y);
      markScratched(x, y, x, y);
      maybeStartShine();
      // Unconditional — React no-ops on identical state. Conditional set
      // with `if (!hintDismissed)` would force `hintDismissed` into the
      // useEffect deps, and any state change there tears down the bake
      // canvas + grid + mask between taps. That was the "first tap doesn't
      // scratch" bug (2026-06-07): tap 1 stamped the mask, then immediately
      // triggered the cleanup that threw away that mask.
      setHintDismissed(true);
      // Same once-per-mount semantics — the parent's onScratchStart fires
      // when the user first touches the foil. Uses the same dismissal
      // signal as `hintDismissed` to dedupe across pointer events.
      if (!startNotifiedRef.current) {
        startNotifiedRef.current = true;
        onScratchStartRef.current?.();
      }
      needsFrame = true;
      ensureRunning();
      e.preventDefault();
    }

    function onPointerMove(e: PointerEvent) {
      if (!pressed || e.pointerId !== activePointerId) return;
      const { x, y } = pointToLogical(e);
      if (!Number.isFinite(lastX) || !Number.isFinite(lastY)) {
        lastX = x; lastY = y;
      }
      // Emit BEFORE marking (see onPointerDown comment for why).
      emissionLeftover = walkEmissions(lastX, lastY, x, y, emissionLeftover);
      stampMaskSegment(lastX, lastY, x, y);
      markScratched(lastX, lastY, x, y);
      lastX = x; lastY = y;
      maybeStartShine();
      needsFrame = true;
      ensureRunning();
      e.preventDefault();
    }

    function onPointerUp(e: PointerEvent) {
      if (e.pointerId !== activePointerId) return;
      pressed = false;
      activePointerId = null;
      lastX = NaN; lastY = NaN;
      try { foilCanvas!.releasePointerCapture(e.pointerId); } catch { /* */ }
      // One last frame to ensure final stamp + any pending shine starts
      needsFrame = true;
      ensureRunning();
    }

    foilCanvas.addEventListener('pointerdown', onPointerDown);
    foilCanvas.addEventListener('pointermove', onPointerMove);
    foilCanvas.addEventListener('pointerup', onPointerUp);
    foilCanvas.addEventListener('pointercancel', onPointerUp);
    // NOTE: deliberately NOT listening to 'pointerleave' — setPointerCapture
    // above keeps events flowing when the finger crosses the canvas edge.
    // Treating pointerleave as end-gesture broke the UX of dragging past the
    // edge and back (the previous version cut the gesture mid-drag).

    // ─── Resize handling ─────────────────────────────────────────────
    // On a layout change (orientation, window resize) we re-bake everything.
    // Strategy: scale the existing mask + grid to the new size so the user
    // doesn't lose their progress. Simple proportional copy.
    const resizeObs = new ResizeObserver(() => {
      const next = getLogicalSize();
      if (Math.round(next.wLog) === Math.round(wLog) &&
          Math.round(next.hLog) === Math.round(hLog)) return;
      // Snapshot mask before resize. Note: the new maskCtx's drawImage of
      // this snapshot uses nearest-neighbor (imageSmoothingEnabled=false
      // below) so the jagged scratch edges survive the scale instead of
      // bilinear-blurring into a soft halo.
      const oldMask = document.createElement('canvas');
      oldMask.width = maskCanvas.width;
      oldMask.height = maskCanvas.height;
      oldMask.getContext('2d')!.drawImage(maskCanvas, 0, 0);
      // Update sizes. Math.ceil (not round) for the same reason as
      // the initial sizing above — prevents the 1px white-line gap
      // at the bottom of landscape-aspect cards.
      wLog = next.wLog; hLog = next.hLog;
      wPx = Math.ceil(wLog * dpr);
      hPx = Math.ceil(hLog * dpr);
      brushR = wLog * BRUSH_R_FRAC;
      cellsX = Math.ceil(wLog / GRID_CELL_PX);
      cellsY = Math.ceil(hLog / GRID_CELL_PX);
      totalCells = cellsX * cellsY;
      // We deliberately do NOT preserve grid contents on resize — recompute
      // scratchedCount from a quick alpha sample so the shine threshold still
      // fires roughly correctly. Simpler than rasterizing the old grid.
      grid = new Uint8Array(totalCells);
      scratchedCount = 0;
      configureCanvas(foilCanvas);
      foilCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Prebake + mask now sized at device pixels (see above for why).
      foilBaked = prebakeFoil(outlineImgRef.current, wPx, hPx);
      maskCanvas.width = wPx;
      maskCanvas.height = hPx;
      maskCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Nearest-neighbor preserves the jagged scratch edges. Default
      // bilinear smoothing would soften them into a blurry halo on
      // orientation change.
      maskCtx.imageSmoothingEnabled = false;
      maskCtx.drawImage(oldMask, 0, 0, wPx, hPx);
      maskCtx.imageSmoothingEnabled = true;
      needsFrame = true;
      ensureRunning();
    });
    resizeObs.observe(card);

    // Expose a re-bake trigger to the outlineImg watcher above. Called
    // when the outline image arrives — re-bakes foilBaked (gradient +
    // outline-multiply this time) and forces one more RAF tick so the
    // updated bake gets composited to the foil canvas.
    requestRebakeRef.current = () => {
      foilBaked = prebakeFoil(outlineImgRef.current, wPx, hPx);
      needsFrame = true;
      ensureRunning();
    };

    // Kick the first render
    ensureRunning();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObs.disconnect();
      requestRebakeRef.current = null;
      foilCanvas.removeEventListener('pointerdown', onPointerDown);
      foilCanvas.removeEventListener('pointermove', onPointerMove);
      foilCanvas.removeEventListener('pointerup', onPointerUp);
      foilCanvas.removeEventListener('pointercancel', onPointerUp);
      // Clear the deferred onScratchComplete timeout if scratching
      // crossed threshold within the last 500 ms but the engine is
      // tearing down before the timeout fires. Avoids invoking the
      // parent callback on a now-unmounted React tree.
      if (completeTimeoutId !== null) {
        clearTimeout(completeTimeoutId);
        completeTimeoutId = null;
      }
    };
    // NOTE: deps deliberately empty — this effect runs once on mount.
    //
    // outlineImg is read via outlineImgRef so a later state update doesn't
    // tear down the engine. The dedicated watcher effect above mirrors
    // state → ref + calls requestRebakeRef to refresh the bake.
    //
    // hintDismissed is also deliberately NOT in deps — see the comment
    // in onPointerDown for the "first tap doesn't scratch" land-mine that
    // included state in deps would re-introduce.
  }, []);

  return (
    <div
      className="card"
      ref={cardRef}
      // Drive the card's width/aspect/viewport-fit clamp via the CSS
      // custom property (see .card rule in styles.css). Setting
      // `aspectRatio` inline would override the CSS calc-with-aspect
      // trick that keeps tall portraits from overflowing the viewport,
      // so we feed the variable instead.
      style={{ ['--card-aspect' as string]: String(aspectRatio) } as React.CSSProperties}
    >
      {/* Single canvas — the colored reveal image is composited INTO
        * this canvas via destination-over inside the render loop (see
        * Layer 3 comment in ScratchPlayer.tsx). The prior separate
        * `<img className="reveal">` sibling layer was removed
        * 2026-06-10 to fix rounded-corner image bleed. With one
        * descendant under the card's `overflow: hidden` rounded clip,
        * there's no second partial-alpha layer for image color to
        * leak through. */}
      <canvas ref={foilRef} className="foil" />
      {/* Scratch-hint finger — direct port of the in-app ScratchHint
        * component (apps/mobile/.../ScratchConversion.tsx, the
        * lower-left → upper-right diagonal-sweep emoji animation).
        * 84px 👆 rotated -12deg, animated via CSS keyframes through
        * the same path the iOS demo follows: start ~(28%, 72%) →
        * peak (62%, 30%) → back → forward. 2s initial delay, fade in
        * 280ms, three sweeps (~2.6s total motion), fade out 360ms.
        * pointer-events: none so it never intercepts touches. Hidden
        * permanently once the user starts scratching (hintDismissed →
        * doesn't re-render). Only mounted once foilPainted so it can't
        * flash over the colored image before the foil canvas commits. */}
      {!hintDismissed && foilPainted && (
        <div className="scratch-finger" aria-hidden="true">
          <span className="scratch-finger-emoji">👆</span>
        </div>
      )}
    </div>
  );
}
